import type { ReactNode } from "react";
import {
  IconContract,
  IconFileInvoice,
  IconNotes,
  IconShoppingCart,
  IconUser,
} from "@tabler/icons-react";
import { Card, Group, Stack, Text, ThemeIcon } from "@/components/ui";
import type { ContratoAnualizado, OrdenDeCompra, SolicitudDetalle } from "@/lib/types";
import { DatosGeneralesContent } from "./DatosGeneralesContent";
import { ProveedorContent } from "./ProveedorContent";
import { CfdiContent } from "./CfdiContent";

function formatMonto(monto: number, moneda: string): string {
  try {
    return new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda }).format(monto);
  } catch {
    return `${monto.toFixed(2)} ${moneda}`;
  }
}

function ContratoAnualizadoContent({ contrato }: { contrato: ContratoAnualizado }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <Campo label="Clave del contrato" valor={contrato.clave} />
      <Campo label="Importe del contrato" valor={formatMonto(contrato.total, contrato.moneda)} />
      <Campo label="Proveedor" valor={`${contrato.proveedorNombre} (#${contrato.proveedorNumero})`} />
      <Campo label="RFC del proveedor" valor={contrato.proveedorRfc} />
    </div>
  );
}

function OrdenDeCompraContent({ orden }: { orden: OrdenDeCompra }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <Campo label="Clave de la orden" valor={orden.clave} />
      <Campo label="Importe de la orden" valor={formatMonto(orden.total, orden.moneda)} />
      <Campo label="Proveedor" valor={`${orden.proveedorNombre} (#${orden.proveedorNumero})`} />
      <Campo label="RFC del proveedor" valor={orden.proveedorRfc} />
    </div>
  );
}

function Campo({ label, valor }: { label: string; valor: string }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={500}>{valor}</Text>
    </Stack>
  );
}

/**
 * Secciones del resumen (orden/contrato/datos/proveedor/CFDI) · cada una se
 * monta solo si la solicitud ya tiene ese dato. Reemplazo del `Accordion`
 * de Mantine: en el original TODAS las secciones venían abiertas por
 * default y nada las colapsaba en ningún flujo real, así que acá se
 * simplifica a cards siempre expandidas (mismo resultado visual, sin la
 * complejidad de un accordion multi-item que nadie interactúa).
 */
export function ResumenAccordion({ sol }: { sol: SolicitudDetalle }) {
  return (
    <Stack gap="sm">
      {sol.ordenCompra && (
        <Card padding="md">
          <AccordionHeader
            icon={<IconShoppingCart size={16} stroke={1.6} />}
            color="exito"
            titulo="Orden de compra"
            subtitulo={`${sol.ordenCompra.clave} · ${sol.ordenCompra.proveedorNombre}`}
            truncateSubtitulo
          />
          <div className="mt-3">
            <OrdenDeCompraContent orden={sol.ordenCompra} />
          </div>
        </Card>
      )}
      {sol.contratoAnualizado && (
        <Card padding="md">
          <AccordionHeader
            icon={<IconContract size={16} stroke={1.6} />}
            color="exito"
            titulo="Contrato anualizado"
            subtitulo={`${sol.contratoAnualizado.clave} · ${sol.contratoAnualizado.proveedorNombre}`}
            truncateSubtitulo
          />
          <div className="mt-3">
            <ContratoAnualizadoContent contrato={sol.contratoAnualizado} />
          </div>
        </Card>
      )}
      {sol.datosGenerales && (
        <Card padding="md">
          <AccordionHeader
            icon={<IconNotes size={16} stroke={1.6} />}
            color="azulProfundo"
            titulo="Datos generales"
            subtitulo="Motivo · periodo · área"
          />
          <div className="mt-3">
            <DatosGeneralesContent dg={sol.datosGenerales} />
          </div>
        </Card>
      )}
      {sol.proveedor && (
        <Card padding="md">
          <AccordionHeader
            icon={<IconUser size={16} stroke={1.6} />}
            // `slate` de marca · la paleta no tiene familia violeta legible.
            color="slate"
            titulo="Proveedor asignado"
            subtitulo={`#${sol.proveedor.numeroProveedor} · ${sol.proveedor.razonSocial}`}
            truncateSubtitulo
          />
          <div className="mt-3">
            <ProveedorContent prov={sol.proveedor} />
          </div>
        </Card>
      )}
      {sol.cfdi && (
        <Card padding="md">
          <AccordionHeader
            icon={<IconFileInvoice size={16} stroke={1.6} />}
            color="multiva"
            titulo="CFDI cargado"
            subtitulo={<>Status fiscal: <Text span fw={500}>{sol.cfdi.statusFiscal}</Text></>}
          />
          <div className="mt-3">
            <CfdiContent cfdi={sol.cfdi} />
          </div>
        </Card>
      )}
    </Stack>
  );
}

function AccordionHeader({
  icon,
  color,
  titulo,
  subtitulo,
  truncateSubtitulo,
}: {
  icon: ReactNode;
  color: string;
  titulo: string;
  subtitulo: ReactNode;
  truncateSubtitulo?: boolean;
}) {
  return (
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon variant="filled" color={color} size="md" radius="md">{icon}</ThemeIcon>
      <Stack gap={0} className="min-w-0">
        <Text fw={600} size="sm">{titulo}</Text>
        <Text size="xs" c="dimmed" truncate={truncateSubtitulo}>{subtitulo}</Text>
      </Stack>
    </Group>
  );
}
